/**
 * Regional sale — client display copy of the server table.
 *
 * Time-boxed sale: 50% off Level 1 LIFETIME ($99 → $49) in 15 countries; monthly, L2, L3 and
 * the package stay list. Keep in sync with netlify/functions/_regionalPricing.js (guarded by
 * src/constants/regionalPricing.test.js). The client NEVER decides its own tier: it only
 * receives `pricingTier` + `pricingSaleEndsAt` from /api/geo (server-resolved) and hides the
 * sale once that end time passes. Checkout is always re-quoted on the server.
 */

export const LIST_TIER = 'T1';

/** Public disclosure (Terms, crawler shell, llms.txt). Never includes a sale price. */
export const REGIONAL_PRICING_NOTE =
  'Prices shown are list prices in USD. Regional pricing may apply in some countries; the price shown at checkout applies.';

export const REGIONAL_PRICING_TIERS = {
  T3: ['TH', 'PH', 'ID', 'VN', 'LK', 'KE', 'GH', 'MA', 'UA'],
  T4: ['IN', 'PK', 'NP', 'BD', 'NG', 'EG'],
};

export const REGIONAL_TIER_PRICES_USD = {
  T3: { 'l1-annual': 49 },
  T4: { 'l1-annual': 49 },
};

/** Marketing figure for $99 → $49 (owner copy: "50% off"). */
export const REGIONAL_SALE_PERCENT_OFF = 50;

/** Sale tiers lead with the one-time lifetime price (monthly secondary). */
export const LIFETIME_FIRST_TIERS = ['T3', 'T4'];

export function tierPriceUsd(skuId, tier) {
  const price = REGIONAL_TIER_PRICES_USD[tier]?.[skuId];
  return Number.isFinite(price) ? price : null;
}

export function isDiscountTier(tier) {
  return Boolean(tier && tier !== LIST_TIER && REGIONAL_TIER_PRICES_USD[tier]);
}

export function leadsWithLifetime(tier) {
  return LIFETIME_FIRST_TIERS.includes(tier);
}

/** "India" from "IN" (falls back to the code when Intl.DisplayNames is unavailable). */
export function countryDisplayName(code) {
  const value = String(code || '').trim().toUpperCase();
  if (!value) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(value) || value;
  } catch {
    return value;
  }
}

/**
 * Short end date + time in the user's locale, e.g. "31 Oct, 11:59 pm" / "Oct 31, 11:59 PM".
 * `timeZone` is the sale country's IANA zone from /api/geo (local-deadline mode), so the
 * country's own wall-clock deadline is shown; without it the browser's zone is used.
 */
export function formatSaleEnd(endsAt, locale, timeZone) {
  const ms = Date.parse(endsAt || '');
  if (!Number.isFinite(ms)) return '';
  const opts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat(locale, timeZone ? { ...opts, timeZone } : opts).format(new Date(ms));
  } catch {
    try {
      return new Intl.DateTimeFormat(locale, opts).format(new Date(ms));
    } catch {
      return new Date(ms).toISOString().slice(0, 16).replace('T', ' ');
    }
  }
}

/** "50% off lifetime in India — until 31 Oct, 11:59 pm" (locale-formatted). */
export function regionalSaleLabel(country, endsAt, locale, timeZone) {
  const name = countryDisplayName(country);
  const until = formatSaleEnd(endsAt, locale, timeZone);
  return `${REGIONAL_SALE_PERCENT_OFF}% off lifetime${name ? ` in ${name}` : ''}${until ? ` — until ${until}` : ''}`;
}
